import React, { useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useGetUserBundlesQuery } from '@features/subscriptions';
import { useIsAuthenticated, useUserState } from '@features/user';
import type { StatsigUser } from '@jotpsych/statsig';
import { useStatsigUserSync } from '@jotpsych/statsig';
import { UTMTracker } from '@services/utm';
import { parseUTCDate } from '@utils/dateUtils';

import { CenteredSpinner } from '../../../components/ui/feedback/CenteredSpinner';

/**
 * Gates rendering until Statsig is initialized with the authenticated user.
 *
 * Wraps content that uses feature gates and needs accurate user targeting.
 * Delegates the sync mechanism (stable-hash memo, ready gate, fail-open) to
 * `useStatsigUserSync` from the shared package — this component owns only
 * the JotPsych-specific user-object builder and loading fallback.
 */
export const StatsigUserGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { user: auth0User } = useAuth0();
  const userState = useUserState({
    skip: !isAuthenticated,
  });
  const { data: userBundlesData } = useGetUserBundlesQuery(undefined, {
    skip: !isAuthenticated,
  });

  const userBundles = useMemo(() => {
    if (!userBundlesData?.bundles) return { tier: null, addons: [] };

    const tier = userBundlesData.bundles.find(b => b.bundle_type === 'tier');
    const addons = userBundlesData.bundles
      .filter(b => b.bundle_type === 'add_on')
      .map(b => b.bundle_id);

    return {
      tier: tier?.bundle_id ?? null,
      addons,
    };
  }, [userBundlesData]);

  const statsigUser = useMemo<StatsigUser | null>(() => {
    if (!userState?.user_id) return null;

    const currentUTMs = UTMTracker.getUTMs();

    return {
      userID: userState.user_id,
      customIDs: {
        organizationID: userState.organization_id ?? undefined,
      },
      email: userState.userInfo?.email || auth0User?.email,
      custom: {
        // Also present as customIDs.organizationID; duplicated here because
        // the event backbone's statsig webhook reads the tenant from the
        // delivered user object to stamp organization_id on v2 envelopes
        // (GDN-2622), and `custom` is the contract it relies on.
        organization_id: userState.organization_id ?? undefined,

        specialization: userState.userInfo?.specialization,
        roles: userState.roles,

        paymentStatus: userState.paymentStatus,
        emailVerified: userState.emailVerified,

        // TODO: remove once we migrate Statsig to use organizationID.
        groupIds: userState.group_ids,

        organization_size: userState.organization_info?.size,
        organization_group: userState.organization_info?.group,

        patientMatchEnrolled: userState.patientMatchEnrolled,
        clearinghouseLinked: userState.clearinghouseLinked,

        created_at: userState.createdAt ?? undefined,
        is_new_user: !userState.onboardingSteps?.account_creation_onboarding_completed,
        signup_timestamp: userState.createdAt
          ? Math.floor(parseUTCDate(userState.createdAt).getTime() / 1000)
          : undefined,
        days_since_signup: userState.createdAt
          ? Math.floor(
              (Date.now() - parseUTCDate(userState.createdAt).getTime()) / (1000 * 60 * 60 * 24),
            )
          : undefined,

        ...currentUTMs,

        ...userState.feature_flags,

        subscription_tier: userBundles.tier ?? undefined,
        subscription_addons: userBundles.addons,
      },
    };
  }, [userState, auth0User?.email, userBundles]);

  const { isReady } = useStatsigUserSync({ user: statsigUser, isAuthenticated });

  if (!isReady) {
    return <CenteredSpinner sx={{ height: '100dvh' }} />;
  }

  return <>{children}</>;
};
